import { Injectable } from '@angular/core';
import { SearchTree, SearchSymbolResults, SNode } from './symbol-search';
import { ConfigurationService } from '../../providers/configuration/configuration.service';
import { SymbolLoadService } from './symbol-load.service';
import { FilterOptions, OffsetOptions, OPTION_TYPE, OrderCategory, SEARCH_SYMBOL_INSTRUMENT_TYPE, StrikePriceFilter } from './search-symbol.enums';
import { BehaviorSubject } from 'rxjs';
import { DEFAULT_LOT_SIZE, Utils, INDEX_SYMBOLS_ORDER, MOMENT_OPTION_FORMAT, CURRENT_MARKET, SymbolSearchOrderMarket, SymbolSearchOrderInstrumentType, SymbolSearchOrderContractType } from '@app/_helpers';
import * as moment from 'moment';
import { ExpiryType } from '../../../../utProto/webApiStrategyEnums_pb';
import { MarketIDMap } from '@app/_shared/shared.enums';
import { EXCHANGE_STRINGS } from '@app/_shared/modules/portfolio-form/portfolio-form.enums';
import { ExchangeId } from '../../../../utProto/enums_pb';
import { IndexScripMap } from '@app/modules/trading/modules/pay-off-curve/modules/rapid/providers/legs/legs.constants';
import { Series } from '@app/app.structs';

declare const resultant;
declare const index_resultant;
const MAX_SEARCH_RESULTS = 30;
declare const symbol_info;
declare const indices_info;
declare const indices_cp_info;

@Injectable({
    providedIn: 'root'
})
export class SymbolSearchService {
    symbolInfoLoaded: boolean = false;
    onLoad = new BehaviorSubject<boolean>(false);
    onSymbolInfoLoad = new BehaviorSubject<boolean>(false);
    symbolsLoaded: boolean = false;

    searchedSymbols: SearchSymbolResults[];
    symbolTrie: SearchTree = new SearchTree();
    marketTrie: SearchTree = new SearchTree();
    priceTrie: SearchTree = new SearchTree();
    dateTrie: SearchTree = new SearchTree();
    strikeTrie: SearchTree = new SearchTree();
    optionTrie: SearchTree = new SearchTree();
    seriesTrie: SearchTree = new SearchTree();
    formattedSymbolInfo: Object = {};


    spreadList: Set<string | number> = new Set();
    scripMaster: object = {};
    symbolSearchResultsarray: SearchSymbolResults[] = [];
    symbolSearchResultsMap = new Map<string, SearchSymbolResults>();
    noSymbolFound = false;
    filteredSymbols = [];
    MAX_SEARCH_RESULTS = MAX_SEARCH_RESULTS;
    symbolInfoMap = new Map<string, number[]>()
    indicesMap = new Map<string, number>()
    indicesClosePriceMap = new Map<string, number>()
    expiryMap = new Map<number, SearchSymbolResults[]>()
    searchedValue: string;

    constructor(
        private symbolLoadService: SymbolLoadService,
        private configurationService: ConfigurationService
    ) {
        this.loadData();
    }

    loadData() {
        this.symbolLoadService.load(this.configurationService.applicationUrls.symbolInfoMaster).then(() => {
            Object.keys(symbol_info).forEach(key => {
                this.symbolInfoMap.set(key, symbol_info[key]);
            })
            this.onSymbolInfoLoad.next(true);
        });

        // You can load multiple scripts by just providing the key as argument into load method of the service
        this.symbolLoadService.load(this.configurationService.applicationUrls.scripMaster).then(data => {

            /**
             * data is filled into tries to be accessed further
             */

            Object.keys(resultant).forEach(market => {
                const marketObject = resultant[market];
                Object.keys(marketObject).forEach(symbol => {

                    const commonStockGroup = marketObject[symbol][0];
                    const futureGroup = marketObject[symbol][1];
                    const optionGroup = marketObject[symbol][2];
                    if (commonStockGroup.length > 0) {
                        commonStockGroup.forEach(groupName => {
                            const symbolObject: SearchSymbolResults = {
                                market_id: market,
                                security_id: groupName[1],
                                symbol,
                                series: groupName[0],
                                company_name: groupName[3],
                                instrument_type: SEARCH_SYMBOL_INSTRUMENT_TYPE.COMMON_STOCK,
                                scrip_name: symbol + ' ' + groupName[0],
                                symbol_id: Utils.getSymbolId(MarketIDMap[market], groupName[1]),
                                lot_size: null,
                                is_index: false
                            };
                            // maintaining a symbolSearchResultsarray array to be used for fallback method
                            this.symbolSearchResultsarray.push(symbolObject);
                            this.mapSet(symbolObject);
                            this.scripMaster[market + '_' + groupName[1]] = symbolObject;
                            this.marketTrie.insert(market, symbolObject);
                            this.symbolTrie.insert(symbol, symbolObject);
                            this.seriesTrie.insert(groupName[0], symbolObject);
                        });
                    }

                    // Insert Futures into SearchTree
                    if (futureGroup.length > 0) {
                        futureGroup.forEach(groupName => {
                            const symbolObject: SearchSymbolResults = {
                                market_id: market,
                                security_id: groupName[2],
                                symbol,
                                expiry: groupName[0],
                                instrument_type: SEARCH_SYMBOL_INSTRUMENT_TYPE.FUTURE,
                                scrip_name: symbol + ' ' + groupName[0] + (groupName[1] !== 0 ? groupName[1] : ''),
                                series: null,
                                symbol_id: Utils.getSymbolId(MarketIDMap[market], groupName[2]),
                                lot_size: null,
                                is_index: false
                            };
                            // maintaining a symbolSearchResultsarray array to be used for fallback method
                            this.symbolSearchResultsarray.push(symbolObject);
                            this.mapSet(symbolObject);
                            this.scripMaster[market + '_' + groupName[2]] = symbolObject;
                            this.marketTrie.insert(market, symbolObject);
                            this.symbolTrie.insert(symbol, symbolObject);
                            this.dateTrie.insert(groupName[0], symbolObject);
                            this.dateTrie.insert(groupName[0].slice(2, 5), symbolObject);
                            if (groupName[1]) {
                                this.dateTrie.insert(groupName[1], symbolObject);
                                this.dateTrie.insert(groupName[1].slice(2, 5), symbolObject);
                                this.spreadList.add(market + '_' + groupName[2]);
                            }
                        });
                    }
                    // Insert Options into SearchTree
                    if (optionGroup.length > 0) {
                        optionGroup.forEach(groupName => {
                            const symbolObject: SearchSymbolResults = {
                                market_id: market,
                                security_id: groupName[3],
                                symbol,
                                expiry: groupName[0],
                                strike_price: groupName[1],
                                option_type: groupName[2],
                                instrument_type: SEARCH_SYMBOL_INSTRUMENT_TYPE.OPTION,
                                scrip_name: symbol + ' ' + groupName.slice(0, 3).join(' '),
                                series: null,
                                symbol_id: Utils.getSymbolId(MarketIDMap[market], groupName[3]),
                                lot_size: null,
                                is_index: false
                            };
                            // maintaining a symbolSearchResultsarray array to be used for fallback method
                            this.symbolSearchResultsarray.push(symbolObject);
                            this.mapSet(symbolObject);
                            this.scripMaster[market + '_' + groupName[3]] = symbolObject;
                            this.marketTrie.insert(market, symbolObject);
                            this.symbolTrie.insert(symbol, symbolObject);
                            this.dateTrie.insert(groupName[0], symbolObject);
                            this.dateTrie.insert(groupName[0].slice(2, 5), symbolObject);
                            this.strikeTrie.insert(groupName[1], symbolObject);
                            this.optionTrie.insert(groupName[2], symbolObject);
                        });
                    }
                });
            });
            this.onLoad.next(true);
            // Script Loaded Successfully
        }).catch(error => console.log(error));

        this.symbolLoadService.load(this.configurationService.applicationUrls.indicesMaster).then(() => {
            Object.keys(index_resultant).forEach(market => {
                Object.keys(index_resultant[market]).forEach(symbol => {
                    // Indices have spaces in them so adding without spaces.
                    const joinedString = symbol.split(' ').join('');

                    const symbolObject: SearchSymbolResults = {
                        market_id: market,
                        security_id: index_resultant[market][symbol],
                        symbol: joinedString,
                        series: '',
                        instrument_type: SEARCH_SYMBOL_INSTRUMENT_TYPE.INDEX,
                        scrip_name: joinedString,
                        symbol_id: Utils.getSymbolId(MarketIDMap[market], index_resultant[market][symbol]),
                        lot_size: null,
                        is_index: true,
                    };
                    this.symbolSearchResultsarray.push(symbolObject);
                    this.mapSet(symbolObject);
                    this.scripMaster[market + '_' + index_resultant[market][symbol]] = symbolObject;
                    this.marketTrie.insert(market, symbolObject);
                    this.symbolTrie.insert(joinedString, symbolObject);
                });
            });
            Object.keys(indices_info).forEach(key => {
                this.indicesMap.set(key, indices_info[key]);
            })

            Object.keys(indices_cp_info).forEach(key => {
                this.indicesClosePriceMap.set(key, indices_cp_info[key]);
            })

        });
    }

    /**
     * RA-987 In case saved algos page was refreshed, the data was rendered before symbol info
     * could be loaded into memory.
     *
     * Added a resolver in routing module which is resolved only after this data is loaded into
     * memory.
     *
     * This function returns a promise which is resolved only after data is loaded
     */
    isDataLoaded(): Promise<any> {
        const symbolInfoPromise = new Promise(resolve => {
            this.onSymbolInfoLoad.subscribe(loaded => {
                if (loaded) {
                    this.symbolInfoLoaded = true;
                    resolve(loaded);
                }
            })
        })
        const symbolPromise = new Promise(resolve => {
            this.onLoad.subscribe(loaded => {
                if (loaded) {
                    this.symbolsLoaded = true;
                    resolve(loaded);
                }
            })
        })

        return new Promise(resolve => {
            Promise.all([symbolInfoPromise, symbolPromise]).then(() => {
                console.log('Symbol Data Loaded');
                resolve('');
            })
        })

    }

    getSymbol(key: string) {
        return this.scripMaster[key];
    }

    isWeeklyExpiry(expiryDate: Date, expiryList: string[]): boolean {
        // If no expiry list is provided, return false
        if (!expiryList?.length) {
            return false;
        }

        // Filter expiries matching the same month and year
        const sameMonthExpiries = expiryList.filter(expiry => {
            const date = new Date(expiry);
            return date.getMonth() === expiryDate.getMonth() &&
                date.getFullYear() === expiryDate.getFullYear();
        });

        // Get the last day of the month
        const lastDay = new Date(expiryDate.getFullYear(), expiryDate.getMonth() + 1, 0);

        // If this expiry date is the last expiry of the month, it's a monthly expiry
        const isLastExpiryOfMonth = sameMonthExpiries.every(expiry => {
            const date = new Date(expiry);
            return date <= expiryDate;
        });

        // If the expiry date is the last day of month, it's a monthly expiry
        const isLastDayOfMonth = expiryDate.getDate() === lastDay.getDate();

        // If either condition is true, it's a monthly expiry, otherwise weekly
        return !(isLastExpiryOfMonth || isLastDayOfMonth);
    }
    
    /**
     * Checks if an expiry date is a weekly expiry or monthly expiry
     * @param expiryDate The expiry date to check
     * @param expiryList List of all expiry dates for the instrument
     * @returns boolean - true if weekly expiry, false if monthly expiry
     * 
     * A date is considered a monthly expiry if either:
     * 1. It is the last expiry date of that month in the expiry list
     * 2. It falls on the last day of the month
     * Otherwise it is considered a weekly expiry
     * 
     * @example
     * // Returns true for a weekly expiry date
     * isWeeklyExpiry(new Date('2023-04-20'), ['2023-04-13', '2023-04-20', '2023-04-27'])
     * 
     * // Returns false for a monthly expiry date
     * isWeeklyExpiry(new Date('2023-04-27'), ['2023-04-13', '2023-04-20', '2023-04-27'])
     */


    getFormattedSymbol(key: string) {
        // If the formatted symbol is already present, return it
        if (this.formattedSymbolInfo && this.formattedSymbolInfo[key]) {
            return this.formattedSymbolInfo[key];
        }
        const symbol = this.getSymbol(key);

        if (symbol?.instrument_type === 'future') {
            const futures = this.advanceSearch(`${symbol.market_id} ${symbol.symbol}`, SEARCH_SYMBOL_INSTRUMENT_TYPE.FUTURE, FilterOptions.expiry);

            if (symbol?.contract_type === OrderCategory.spread) {
                this.formattedSymbolInfo[key] = symbol.scrip_name;
                return this.formattedSymbolInfo[key];
            }

            // Extract month and year from expiry
            const expiryDate = new Date(symbol.expiry);
            const month = expiryDate.toLocaleString('en-US', { month: 'short' }).toUpperCase();
            const year = expiryDate.getFullYear();
            const currentYear = new Date().getFullYear();
            const date = expiryDate.getDate().toString().padStart(2, '0');

            // Check if weekly expiry
            const isWeekly = this.isWeeklyExpiry(expiryDate, futures.map(el => el.expiry));

            // Format the future text
            const futureText = isWeekly
                ? `${date} ${month} FUT`
                : `${month}${year > currentYear ? year.toString().slice(-2) : ''} FUT`;

            this.formattedSymbolInfo[key] = `${symbol.symbol} ${futureText}`;
            return this.formattedSymbolInfo[key];
        }
        else if (symbol?.instrument_type === 'option') {
            const options = this.advanceSearch(`${symbol.market_id} ${symbol.symbol}`, SEARCH_SYMBOL_INSTRUMENT_TYPE.OPTION, FilterOptions.expiry);

            // Extract expiry details
            const expiryDate = new Date(symbol.expiry);
            const month = expiryDate.toLocaleString('en-US', { month: 'short' }).toUpperCase();
            const date = expiryDate.getDate().toString().padStart(2, '0'); // padStart to ensure 2 digits for date
            const year = expiryDate.getFullYear();
            const currentYear = new Date().getFullYear();

            // Check if weekly expiry
            const isWeekly = this.isWeeklyExpiry(expiryDate, options.map(el => el.expiry));

            // Format the expiry text
            const expiryText = isWeekly
                ? `${date} ${month}${year > currentYear ? year.toString().slice(-2) : ''}`
                : `${month}${year > currentYear ? year.toString().slice(-2) : ''}`;

            this.formattedSymbolInfo[key] = `${symbol.symbol} ${expiryText} ${Math.floor(symbol.strike_price)} ${symbol.option_type}`;
            return this.formattedSymbolInfo[key];
        }
        else {
            if ((symbol?.series) && (symbol?.series !== Series.EQ && symbol?.series !== Series.A)) {
                this.formattedSymbolInfo[key] = symbol.scrip_name;
                    return this.formattedSymbolInfo[key];
            }

            this.formattedSymbolInfo[key] = symbol?.symbol;
            return this.formattedSymbolInfo[key];
        }
    }

    mapSet(symbolObj: SearchSymbolResults) {
        this.symbolSearchResultsMap.set(`${symbolObj.market_id}__${symbolObj.security_id}`, symbolObj);
    }


    _searchSymbols(value: string[], returnAll?, marketKeyword = 'NSEFO', instrumentType = SEARCH_SYMBOL_INSTRUMENT_TYPE.ALL,
        contractType = OrderCategory.single, displayField = 'symbol') {
        /**
        This function iterates through filtered set of scrip names and matches pattern based on REGEX.Common function for simple as well as
        advanced search.
        */
        this.noSymbolFound = false;
        let allMarketSymbols: (string | number)[];
        const marketResult = this.marketTrie.search(marketKeyword);
        allMarketSymbols = this.marketTrie.getWords(marketResult.node, marketKeyword);
        let underlyingValue: string = "";
        for (let [objKey, objVal] of Object.entries(IndexScripMap)) {
            if (objVal.toUpperCase() === value[0]) {
                underlyingValue = objKey.toUpperCase();
                break;
            }
        }
        const marketSymbols = new Set(allMarketSymbols);
        let symbols: Set<string | number> = new Set();
        for (let i = 0; i < value.length; i++) {

            const symbolNode = this.symbolTrie.search(value[i]);
            if (symbolNode.node) {
                const symbolResult = new Set(this.symbolTrie.getWords(symbolNode.node, symbolNode.retString));
                symbols = new Set(Array.from(marketSymbols).filter(x => symbolResult.has(x)));
            }
        }
        if (underlyingValue) {
            const underlyingSymbolNode = this.symbolTrie.search(underlyingValue);
            if (underlyingSymbolNode.node) {
                const symbolResult = new Set(this.symbolTrie.getWords(underlyingSymbolNode.node, underlyingSymbolNode.retString));
                symbols = new Set((Array.from(marketSymbols).filter(x => symbolResult.has(x))).concat(Array.from(symbols)));
            }
        }

        if (symbols.size) {
            if (contractType === OrderCategory.spread) {
                symbols = new Set(Array.from(symbols).filter((x: any) => this.spreadList.has(x)));
            } else if (contractType === OrderCategory.single) {
                symbols = new Set(Array.from(symbols).filter((x: any) => !this.spreadList.has(x)));
            }
            let symbolsArray = Array.from(symbols).map((x: any) => this.scripMaster[x]);
            // in case no specific instrument type is provided, return symbols of every instrument type
            // array order is according to the requirements provided in UT-3067
            if (instrumentType === SEARCH_SYMBOL_INSTRUMENT_TYPE.ALL) {
                const arr1: SearchSymbolResults[] = [];
                const arr2: SearchSymbolResults[] = [];
                const arr3: SearchSymbolResults[] = [];
                const arr4: SearchSymbolResults[] = [];
                const arr5: SearchSymbolResults[] = [];
                for (let symbol of symbolsArray) {
                    switch (symbol.instrument_type) {
                        case SEARCH_SYMBOL_INSTRUMENT_TYPE.INDEX:
                            arr1.push(symbol);
                            break;
                        case SEARCH_SYMBOL_INSTRUMENT_TYPE.COMMON_STOCK:
                            arr2.push(symbol);
                            break;
                        case SEARCH_SYMBOL_INSTRUMENT_TYPE.OPTION:
                            arr3.push(symbol);
                            break;
                        case SEARCH_SYMBOL_INSTRUMENT_TYPE.FUTURE:
                            arr4.push(symbol);
                            break;
                        default:
                            // in case symbol is not of any of the above types
                            arr5.push(symbol);
                            break;
                    }
                }
                symbolsArray = arr1.concat(arr2, arr3, arr4, arr5);
            } else {
                // return symbols of specific instrument type
                symbolsArray = symbolsArray.filter((x: any) => x.instrument_type === instrumentType);
            }

            if (displayField && !returnAll) {
                symbolsArray = Utils.getUnique(symbolsArray, displayField);
            }
            if (!returnAll) {
                symbolsArray = symbolsArray.splice(0, MAX_SEARCH_RESULTS);
            }

            this.searchedSymbols = symbolsArray;
            this.filteredSymbols = symbolsArray;
        } else {
            this.noSymbolFound = true;
            return [];
        }
        return this.filteredSymbols;
    }

    /**
     * function used for getting strike price list or expiry list
     * based on the arguments passed to the function
     * value is the combination of exchangeId + symbol
     */
    getStrikePriceExpiryList(value: string, instrument_type: SEARCH_SYMBOL_INSTRUMENT_TYPE, option_type?: OPTION_TYPE | string, key?: FilterOptions,
        offset?: OffsetOptions, strikePriceOffset?: StrikePriceFilter, expiry?: string) {
        let searchResult = this.advanceSearch(value, instrument_type, FilterOptions.expiry, option_type)


        /**
         * filterOptions.basic: if this option is selected by user, returning expiry list firstly
         * later based on the strikePriceOffset reurn expected strike price result.
         * filterOptions.monthly: if user selects monthly then there are three options for
         * expiryOffset
         * offsetOptions.Current: if current is selected then returning strike prices result
         * for current month expiries.
         * offsetOptions.Next: if Next is selected then returning strike prices result
         * for next month expiries.
         * offsetOptions.Far: if Far is selected then returning strike prices result
         * for 3rd month expiries from current month .
         * filterOptions.weekly: if user selects weekly then there are four options for
         * expiryOffset.
         * offsetOptions.Current: if current is selected then returning strike prices result
         * for current week expiry.
         * offsetOptions.Next: if Next is selected then returning strike prices result
         * for next week expiry.
         * offsetOptions.Far: if Far is selected then returning strike prices result
         * for 3rd week expiry from current week .
         * offsetOptions.FarFar: if FarFar is selected then returning strike prices result
         * for 4th week expiry from current week .
         */
        switch (key) {
            case FilterOptions.basic:
                /**
                 * if strikePrice offset is sent in params then filtering strike price based on the expiry selected.
                 * else sending expiry list.
                 */
                if (strikePriceOffset) {
                    searchResult = this.advanceSearch(value, instrument_type, FilterOptions.strike_price, option_type, expiry);
                    break;
                }
                else {
                    if (searchResult && searchResult.length) {
                        return searchResult.map(r => r.expiry).filter((v, i, a) => a.indexOf(v) === i);
                    }
                    return [];
                }

            case FilterOptions.weekly:
                const expiriesList = searchResult.map(el => el.expiry).filter((v, i, a) => a.indexOf(v) === i);
                const expiryDay = this.getSelectedExpiry(0, offset, searchResult[0].symbol, expiriesList);
                searchResult = searchResult.filter(el => el.expiry === expiryDay);
                break;

            case FilterOptions.monthly:
                const expiries = searchResult.map(el => el.expiry).filter((v, i, a) => a.indexOf(v) === i);
                const expiryDate = this.getSelectedExpiry(1, offset, searchResult[0].symbol, expiries);
                searchResult = searchResult.filter(el => el.expiry === expiryDate);
                break;
        }

        if (searchResult && searchResult.length) {
            /**
             * function returns either strike price list or ATM list with offset value
             * based on the strikePriceOffset option provided.
             */
            return Utils.extractStrikePricesList(searchResult, strikePriceOffset);
        }
        return [];
    }


    advanceSearch(value: string, instrument_type: SEARCH_SYMBOL_INSTRUMENT_TYPE, key = FilterOptions.empty, option_type?: OPTION_TYPE | string, expiry?: string, strike_price?: string) {
        /**
         * calling search function only user has changed the serach keyword
         */
        this.searchedValue = value;
        this.searchedSymbols = this.search(value, false, true, true);
        if (this.searchedSymbols && this.searchedSymbols.length) {
            switch (key) {
                case FilterOptions.expiry:
                    if (instrument_type === SEARCH_SYMBOL_INSTRUMENT_TYPE.FUTURE) {
                        this.searchedSymbols = this.searchedSymbols.filter(el => (el.instrument_type === instrument_type) && (`${el.market_id} ${el.symbol}` === value));
                        this.searchedSymbols.sort(Utils.sortExpiry);
                        this.searchedSymbols.forEach((symbol) => {
                            const expiryDateMonth = moment(symbol.expiry, MOMENT_OPTION_FORMAT).month();
                            if (this.expiryMap.has(expiryDateMonth)) {
                                const symbol_data = this.expiryMap.get(expiryDateMonth);
                                this.expiryMap.set(expiryDateMonth, [...symbol_data, symbol]);
                            } else {
                                this.expiryMap.set(expiryDateMonth, [symbol]);
                            }
                        })
                    }
                    else {
                        this.searchedSymbols = this.searchedSymbols.filter(el => el.instrument_type === instrument_type
                            && el.option_type === option_type);
                    }
                    this.searchedSymbols = this.searchedSymbols.sort(Utils.sortExpiry);
                    break;
                case FilterOptions.strike_price:
                    this.searchedSymbols = this.searchedSymbols.filter(el => el.instrument_type === SEARCH_SYMBOL_INSTRUMENT_TYPE.OPTION
                        && el.option_type === option_type && el.expiry === expiry);
                    break;
                // Returns single symbol in array on the basis of static data.
                case FilterOptions.symbol:
                    if (instrument_type === SEARCH_SYMBOL_INSTRUMENT_TYPE.FUTURE) {
                        this.searchedSymbols = this.searchedSymbols.filter(el => el.instrument_type === instrument_type && el.expiry == expiry);
                    }
                    else {
                        this.searchedSymbols = this.searchedSymbols.filter(el => el.instrument_type === instrument_type
                            && (!option_type || el.option_type === option_type) && (!expiry || el.expiry === expiry) && (!strike_price || el.strike_price === strike_price));
                    }
                    break;

                case FilterOptions.index:
                    /**
                     * filtering seached result based on security id 
                     * so that exact result can be passed where ever needed
                     */
                    this.searchedSymbols = this.searchedSymbols.filter(el => el.security_id === this.indicesMap.get(value.split(' ')[1]))
            }
        }
        return this.searchedSymbols;
    }

    search(value: string, searchSymbolFiltered = false, includeIndices = false, returnEntireData = false) {
        /**
         * Scrip Name Search
         */
        const orignalSymbol = value;
        const market = value?.split(' ')[0];
        this.searchedSymbols = [];
        value = value.toUpperCase();
        const searchValues = value.split(' ');
        let symbolsSearched = false;
        let marketSearched = false;
        let results = new Set();
        // tslint:disable-next-line:prefer-for-of
        for (let i = 0; i < searchValues.length; i++) {
            const keyWord = searchValues[i];
            const marketKeyword = this.marketTrie.search(keyWord);
            if (!searchSymbolFiltered) {
                if (marketKeyword.node != null && !marketSearched) {
                    const marketResult = new Set(this.marketTrie.getWords(marketKeyword.node, marketKeyword.retString));
                    // If no result is returned from intersection, then let previous results be shown as result or used
                    // for comparison os next search string. This has been done for all tries.
                    if (!results.size) { results = marketResult; }
                    marketSearched = true;
                    results = new Set(Array.from(results).filter(x => marketResult.has(<string | number>x)));
                    continue;
                }
            }

            const symbolKeyword = this.symbolTrie.search(keyWord);
            if (symbolKeyword.node != null && !symbolsSearched) {
                const symbolResult = new Set(this.symbolTrie.getWords(symbolKeyword.node, symbolKeyword.retString));
                if (!results.size) { results = symbolResult; }
                results = new Set(Array.from(results).filter(x => symbolResult.has(<string | number>x)));
                // Will not be searching in symbol trie once it is searched as it will block option type search (as most
                // strings will be found in this trie)
                symbolsSearched = true;
                continue;
            }
            const optionKeyword = this.optionTrie.search(keyWord);
            if (optionKeyword.node != null) {
                const optionResult = new Set(this.optionTrie.getWords(optionKeyword.node, optionKeyword.retString));
                if (!results.size) { results = optionResult; }
                results = new Set(Array.from(results).filter(x => optionResult.has(<string | number>x)));
                continue;
            }

            const dateKeyword = this.dateTrie.search(keyWord);
            if (dateKeyword.node != null) {
                const dateResult = new Set(this.dateTrie.getWords(dateKeyword.node, dateKeyword.retString));
                if (!results.size) { results = dateResult; }
                results = new Set(Array.from(results).filter(x => dateResult.has(<string | number>x)));
                continue;
            }

            const strikeKeyword = this.strikeTrie.search(keyWord);
            if (strikeKeyword.node != null) {
                const strikeResult = new Set(this.strikeTrie.getWords(strikeKeyword.node, strikeKeyword.retString));
                if (!results.size) { results = strikeResult; }
                results = new Set(Array.from(results).filter(x => strikeResult.has(<string | number>x)));
                continue;
            }

            const seriesKeyword = this.seriesTrie.search(keyWord);
            if (seriesKeyword.node != null) {
                const seriesResult = new Set(this.seriesTrie.getWords(seriesKeyword.node, seriesKeyword.retString));
                if (!results.size) { results = seriesResult; }
                results = new Set(Array.from(results).filter(x => seriesResult.has(<string | number>x)));
                continue;
            }

            results = new Set();
            break;
        }

        let underlyingValResult = [];
        let underlyingValue: string = "";
        for (let [objKey, objVal] of Object.entries(IndexScripMap)) {
            if (objKey.toUpperCase() === value.toUpperCase()) {
                underlyingValue = objVal.toUpperCase();
                break;
            }
        }
        if (underlyingValue) {
            const underlyingSybolKeyword = this.symbolTrie.search(underlyingValue);
            if (underlyingSybolKeyword.node != null) {
                underlyingValResult = Array.from(new Set(this.symbolTrie.getWords(underlyingSybolKeyword.node, underlyingSybolKeyword.retString)));
            }
            if (underlyingValResult.length) {
                results = new Set([underlyingValResult[0]].concat(Array.from(results)));
            }
        }

        const symbolsArray: SearchSymbolResults[] = Array.from(results)
            .map((x: any) => this.scripMaster[x])
            .filter(item => {
                if (item.is_index && includeIndices) {
                    return true;
                } else if (item.is_index) {
                    return false;
                }
                return true;
            });
        this.searchedSymbols = symbolsArray;
        if (this.searchedSymbols.length < MAX_SEARCH_RESULTS) {
            // tslint:disable-next-line:prefer-for-of
            for (let i = 0; i < this.symbolSearchResultsarray.length; i++) {
                if (this.symbolSearchResultsarray[i].scrip_name.includes(value) || (this.symbolSearchResultsarray[i].company_name && this.symbolSearchResultsarray[i].company_name.includes(value))) {
                    this.searchedSymbols.push(this.symbolSearchResultsarray[i]);
                }
                if (this.searchedSymbols.length >= MAX_SEARCH_RESULTS) {
                    break;
                }
            }
            this.searchedSymbols = this.searchedSymbols.filter((item, index) => {
                const firstIndex = this.searchedSymbols.indexOf(item) === index;

                // Only include indices along with symbols if includeIndices is "true".
                if (item.is_index && includeIndices && firstIndex) {
                    return true;
                } else if (item.is_index) {
                    return false;
                }
                return firstIndex;
            });
        }
        if (returnEntireData) {
            if (underlyingValResult.length) {
                let underlyingSymbolResult = this.searchedSymbols.splice(0, 1);
                const result = this.customSort(this.searchedSymbols);
                return underlyingSymbolResult.concat(result);
            } else {
                const result = this.customSort(this.searchedSymbols)
                return result;
            }
        }
        else {
            /**
            * filtering seached result based on security id 
            * so that exact result can be passed where ever needed
            * If symbol present in indicesMap then match by securityID
            * Remove marketID from orignalSymbol -> (orignalSymbol.split(' ')[1])
            */
            if (this.indicesMap.get(orignalSymbol.split(' ')[1]) && CURRENT_MARKET.includes(market)) {
                this.searchedSymbols = this.searchedSymbols.filter(el => el.security_id === this.indicesMap.get(orignalSymbol.split(' ')[1]))
            }
            if (underlyingValResult.length) {
                let underlyingSymbolResult = this.searchedSymbols.splice(0, 1);
                const result = this.customSort(this.searchedSymbols).splice(0, MAX_SEARCH_RESULTS - 1);
                return underlyingSymbolResult.concat(result);
            } else {
                const result = this.customSort(this.searchedSymbols).splice(0, MAX_SEARCH_RESULTS);
                return result;
            }
        }
    }

    findScripsBySecurity(securities: any[]) {
        let results: SearchSymbolResults[] = [];
        const searchSet = securities.map(s => `${s.market_id}__${s.security_id}`);
        searchSet.forEach(el => {
            results.push(this.symbolSearchResultsMap.get(el));
        });
        return results;
    }


    findSingleScripsBySecurity(security: any) {
        let result: SearchSymbolResults;
        let searchSet = `${security.market_id}__${security.security_id}`;
        result = this.symbolSearchResultsMap.get(searchSet);
        return result;
    }

    getSelectedExpiry(expiryType: number, expiryOffset: number, symbol: string, expiries: string[]) {

        if (INDEX_SYMBOLS_ORDER.includes(symbol) && expiryType === ExpiryType.EXPIRYTYPE_MONTHLY) {
            const expectedMonth = moment(expiries[0], MOMENT_OPTION_FORMAT).add(expiryOffset, 'months').month();
            let largestExpiry = '';
            expiries.every((expiry, index) => {
                const iterMonth = moment(expiry, MOMENT_OPTION_FORMAT).month();

                if (expectedMonth === iterMonth) {
                    largestExpiry = expiry;
                    // If month changes during iteration and we already got expiry, break the loop.
                } else if (largestExpiry && expectedMonth !== iterMonth) {
                    return false;
                }
                return true;
            });

            return largestExpiry;

        } else {
            return expiries && expiries[expiryOffset];
        }
    }

    genKey(...args) {
        return `${args.join('_')}`;
    }

    getSymbolLots(...args) {
        const key = this.genKey(...args);
        const symbolInfo = this.symbolInfo(key);
        if (symbolInfo?.length) {
            return symbolInfo[0];
        } else {
            return DEFAULT_LOT_SIZE;
        }
    }

    /**
     * Retrieves the tick size for a symbol.
     * @param args - The arguments ('market_id' and 'security_id') used to generate the key for symbolInfo.
     * @returns The tick size of the symbol, or undefined if symbolInfo is empty.
     */
    getSymbolTickSize(...args): number {
        const key = this.genKey(...args);
        const symbolInfo = this.symbolInfo(key);
        if (symbolInfo?.length) {
            return symbolInfo[1];
        }
    }

    /**
     * @param key key for map reference
     * @return [market_lot, tick_size, scrip_precision]
     */
    symbolInfo(key: string) {
        return this.symbolInfoMap.get(key);
    }

    getSymbolForSymbolId(symbolId: number) {
        return this.symbolSearchResultsarray.find(el => el.symbol_id === symbolId);
    }

    symbolExists(symbol: string) {
        return this.symbolTrie.search(symbol).node != null;
    }

    customSort(originalList: SearchSymbolResults[]) {
        const indexedList: any[] = originalList.map((result, index) => ({
            result,
            index
        }));

        // Sort the indexed list
        indexedList.sort((a, b) => {
            const sortByInstrument = SymbolSearchOrderInstrumentType.indexOf(a.result.instrument_type) - SymbolSearchOrderInstrumentType.indexOf(b.result.instrument_type);
            if (sortByInstrument === 0) {
                const sortByMarket = SymbolSearchOrderMarket.indexOf(a.result.market_id) - SymbolSearchOrderMarket.indexOf(b.result.market_id);
                if (sortByMarket === 0) {
                    const sortBySeries = a.result.series && b.result.series && a.result.series !== b.result.series
                        ? (a.result.series === Series.EQ ? -1 : (b.result.series === Series.EQ ? 1 : 0))
                        : 0;
                    if (sortBySeries === 0) {
                        return a.index - b.index;
                    }
                    return sortBySeries;
                }
                return sortByMarket;
            }
            return sortByInstrument;
        });

        // Convert back to the original list structure
        return indexedList.map(element => element.result);
    }

    searchActiveMonthyExpiry(value: string) {
        let symbolData = []
        if (this.expiryMap.has(moment().month())) {
            symbolData = this.expiryMap.get(moment().month());
            symbolData = symbolData.filter((el) => `${el.market_id} ${el.symbol}` === value);
        }
        //searching for symbols expiry in current month. If there are no expiries we are checking monthly expiry in next month
        if (!symbolData.length) {
            symbolData = this.expiryMap.get(moment().month() + 1);
            symbolData = symbolData.filter((el) => `${el.market_id} ${el.symbol}` === value);
        }
        return symbolData[symbolData.length - 1];
    }
}
